import { jsx } from 'react/jsx-runtime';
import classNames from 'classnames';
import { forwardRef } from 'react';
import { getTestLabels } from '../../utils/getTestLabels.js';
import style from './Tooltip.module.scss.js';

const { elementTestLabel } = getTestLabels('Tooltip');
const getTooltipClasses = (defaultPosition, defaultJustified, tipWidth, breakpointPlacements) => {
    const classList = [style['z-tooltip__popup']];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const allConfigs = {
        ...{ default: { position: defaultPosition, justified: defaultJustified } },
        ...breakpointPlacements,
    };
    Object.keys(allConfigs).forEach((key) => {
        const position = allConfigs[key].position;
        const justified = allConfigs[key].justified;
        const placement = position.split('-')[0];
        const alignment = position.split('-')[1];
        let alignmentDirection = '';
        const breakpointModifier = key === 'default' ? '' : `-bp-${key}`;
        if (alignment === 'middle') {
            alignmentDirection = ['left', 'right'].includes(placement)
                ? '-vert'
                : '-horz';
        }
        if (tipWidth !== 'sm') {
            classList.push(style[`z-tooltip__popup--width-${tipWidth}`]);
        }
        classList.push(style[`z-tooltip__popup--${placement}${breakpointModifier}`]);
        classList.push(style[`z-tooltip__popup--align-${alignment}${alignmentDirection}${breakpointModifier}`]);
        if (justified) {
            classList.push(style[`z-tooltip__popup--justify-${alignment}${breakpointModifier}`]);
        }
    });
    return classList;
};
/**
 * @param {breakpointPlacements} breakpointPlacements - (optional) Can be used to change the tooltip placement at different common minimum breakpoints (xs, sm, md, lg).
 * @param {boolean} justified - (optional) If supplied, will justify the alignment of any non-`middle` value assigned to the `position` prop. It works with the second segment of the `position` prop (eg. `justified={true}` with `position="top-right"` will get right justified alignment).
 * @param {TooltipPosition} position - (optional) One of `bottom-left`, `bottom-middle`, `bottom-right`, `left-bottom`, `left-middle`, `left-top`, `right-bottom`, `right-middle`, `right-top`, `top-left`, `top-middle`, `top-right`. Defaults to `top-middle`.
 * @param {React.ReactNode} tipText - (required) The text/markup content inside the tooltip popup.
 * @param {('sm' | 'md' | 'lg')} tipWidth - (optional) Max width of the tooltip before the text wraps. Can be `sm` (default, max 120px) or `md` (max 180px).
 */
const TooltipPopup = forwardRef((props, ref) => {
    const { className, justified = false, breakpointPlacements, position = 'top-middle', id, tipText, tipWidth = 'sm', ...otherProps } = props;
    return (jsx("div", { "data-zta": elementTestLabel('Text'), id: id, ref: ref, role: "tooltip", className: classNames(className, getTooltipClasses(position, justified, tipWidth, breakpointPlacements)), ...otherProps, children: tipText }));
});
TooltipPopup.displayName = 'TooltipPopup';

export { TooltipPopup, getTooltipClasses };
//# sourceMappingURL=TooltipPopup.js.map
